/**
 * The application shell (docs/03 §6, docs/04 §0/§3). Bands follow the TKC brand
 * standard's breakpoints: bottom nav <768 with the green contextual header ·
 * icon rail 768-991 · full sidebar 992+ (see the theme.css rationale for why
 * the labelled sidebar starts at 992 rather than 1200). Top bar carries search
 * (centred), My Work count, bell, and the account menu — density moved INTO that
 * menu (docs/04 §5): it is a set-once comfort preference, and in the top bar it
 * read as a view control for whatever list you were looking at.
 *
 * TWO scroll regions, not one (ADR-31, superseding docs/03 §6's original "one
 * scroll context per screen"). The shell is exactly one viewport tall and does
 * not scroll; the nav pane and `<main>` each scroll themselves, and the nav only
 * when its own content is taller than the pane. Navigation is a fixed frame:
 * scrolling a long property list should never carry the modules off-screen, and
 * losing the top bar means losing search on the screen where you most want it.
 *
 * ONE account button, top-right (Matt: "I'm still wondering if its redundant to
 * have 2 avatar buttons"). It was redundant. The sidebar foot keeps the identity
 * it always showed — name, role, environment pill, build stamp — as text, with
 * nothing to click.
 *
 * Navigation entries mirror docs/05's sidebar order. Tier 3 modules render
 * locked-with-reason (docs/05: "Invoicing (Tier 3, locked w/ reason)").
 */

import * as RadixDialog from '@radix-ui/react-dialog';
import {
  Building2,
  ChartColumn,
  ClipboardList,
  Files,
  House,
  Lock,
  Menu,
  Palette,
  ReceiptText,
  Search,
  Settings,
  Truck,
  Users,
  UsersRound,
} from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { Link as RouterLink, NavLink, Outlet, useLocation, useNavigate } from 'react-router';

import type { Me } from '../../app/api.js';
import { useBadges } from '../../app/useBadges.js';
import { useHealth } from '../../app/useHealth.js';
import { useApplyTheme, usePreferences } from '../../app/usePreferences.js';
import {
  Avatar,
  BrandMark,
  CountPill,
  TestEnvBanner,
  ToastProvider,
} from '../../components/ui/index.js';
import { QaButton } from '../qa/QaPanel.js';

import { NotificationBell } from './NotificationDrawer.js';
import { UserMenu } from './UserMenu.js';
import { ViewAsBanner } from './ViewAsBanner.js';

interface NavEntry {
  readonly to: string;
  readonly label: string;
  readonly icon: ReactNode;
  readonly lockedReason?: string;
  /** Entry stays locked unless the signed-in user holds this key. */
  readonly permissionKey?: string;
}

const NAV: readonly NavEntry[] = [
  { to: '/', label: 'Dashboard', icon: <House size={19} /> },
  { to: '/properties', label: 'Properties', icon: <Building2 size={19} /> },
  // Matt, 2026-08-26: "we previously agreed to change this to just say
  // 'Tenants' and 'Vendors'". The module IS the record; a lease belongs to a
  // tenant and an agreement to a vendor, so naming the children in the nav made
  // two entries longer than every other one and implied the children were
  // siblings of their parents. Same reasoning as ADR-50, one level up (ADR-52).
  { to: '/tenants', label: 'Tenants', icon: <UsersRound size={19} /> },
  { to: '/vendors', label: 'Vendors', icon: <Truck size={19} /> },
  { to: '/documents', label: 'Documents', icon: <Files size={19} /> },
  // ADR-50: ONE word for dated work. The URL stays `/work` — a route is not a
  // label, and changing it would break every bookmark to buy nothing.
  { to: '/work', label: 'Action items', icon: <ClipboardList size={19} /> },
  { to: '/contacts', label: 'Contacts', icon: <Users size={19} /> },
  {
    to: '/invoicing',
    label: 'Invoicing',
    icon: <ReceiptText size={19} />,
    lockedReason: 'Tier 3 — after launch',
  },
  {
    to: '/reports',
    label: 'Reports',
    icon: <ChartColumn size={19} />,
    lockedReason: 'Tier 3 — after launch',
  },
  {
    to: '/admin',
    label: 'Administration',
    icon: <Settings size={19} />,
    permissionKey: 'admin.home.view',
  },
  { to: '/design', label: 'Design system', icon: <Palette size={19} /> },
];

function SideNavLink({ entry }: { readonly entry: NavEntry }): ReactNode {
  if (entry.lockedReason !== undefined) {
    return (
      /**
       * A locked module is TEXT, not a disabled control — there is nothing here
       * to activate, so it has no widget role and `aria-disabled` on it would
       * be invalid ARIA that assistive tech is free to ignore.
       *
       * The reason therefore has to be in the accessible NAME rather than in a
       * `title`: a title is not reliably announced, and is unreachable entirely
       * by keyboard and by touch. Before this, a screen-reader user heard
       * "Invoicing" and had no way to learn it was locked or why — the lock
       * icon being aria-hidden and the reason living only in the tooltip.
       */
      <span
        title={entry.lockedReason}
        className="flex min-h-11 cursor-not-allowed items-center gap-3 rounded-[10px] px-3 py-2.5 text-[13.5px] font-medium text-on-green-faint"
      >
        <span aria-hidden>{entry.icon}</span>
        <span className="sr-only md:not-sr-only md:grow">{entry.label}</span>
        <Lock size={14} aria-hidden className="hidden md:block" />
        <span className="sr-only">Locked. {entry.lockedReason}</span>
      </span>
    );
  }
  return (
    <NavLink
      to={entry.to}
      end={entry.to === '/'}
      className={({ isActive }) =>
        `flex min-h-11 items-center gap-3 rounded-[10px] px-3 py-2.5 text-[13.5px] font-medium no-underline ` +
        (isActive ? 'bg-brand-sel text-on-green' : 'text-on-green-nav hover:text-on-green')
      }
    >
      <span aria-hidden>{entry.icon}</span>
      <span className="sr-only md:not-sr-only">{entry.label}</span>
    </NavLink>
  );
}

/** Screen-reader announcement on route changes (docs/03 §8, aria-live). */
function RouteAnnouncer(): ReactNode {
  const { pathname } = useLocation();
  const [message, setMessage] = useState('');
  useEffect(() => {
    const segment = pathname.split('/').find((part) => part !== '') ?? 'dashboard';
    setMessage(`Navigated to ${segment.replace(/-/g, ' ')}`);
  }, [pathname]);
  return (
    <div aria-live="polite" role="status" className="sr-only">
      {message}
    </div>
  );
}

/**
 * The bottom-nav "More" sheet (<768): every module that is not in the bar.
 *
 * Below 768 the sidebar and the rail do not render, so without this sheet the
 * only routes a phone can reach are the four in the bar — Tenants, Vendors,
 * Documents, Contacts, Admin and the rest simply do not exist on mobile. The
 * button used to be a placeholder link to /design, which is the bug Matt hit:
 * "tapping menu at bottom left just opens the design system tab".
 *
 * Locked modules render locked WITH their reason as visible text — a phone has
 * no hover for a title tooltip, and docs/03 §4 says locked-with-reason, never
 * hidden.
 */
function MoreSheet({
  entries,
  open,
  onOpenChange,
}: {
  readonly entries: readonly NavEntry[];
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}): ReactNode {
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 bg-brand-950/40" />
        <RadixDialog.Content
          className={
            'fixed inset-x-0 bottom-0 max-h-[80vh] overflow-y-auto rounded-t-dialog bg-card ' +
            'p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] shadow-lg'
          }
        >
          <RadixDialog.Title className="px-1 pb-2 font-display text-h2 text-ink">
            All modules
          </RadixDialog.Title>
          <nav aria-label="More modules" className="flex flex-col">
            {entries.map((entry) =>
              entry.lockedReason === undefined ? (
                <NavLink
                  key={entry.to}
                  to={entry.to}
                  onClick={() => {
                    onOpenChange(false);
                  }}
                  className={({ isActive }) =>
                    // NOT bg-brand-sel: that is the DARK nav's selection tint,
                    // and on this light sheet it fails contrast (axe caught it).
                    `flex min-h-12 items-center gap-3 rounded-[10px] px-3 py-2.5 text-body font-medium no-underline ` +
                    (isActive ? 'bg-surface-hover text-brand-700' : 'text-ink')
                  }
                >
                  <span aria-hidden className="text-ink2">
                    {entry.icon}
                  </span>
                  {entry.label}
                </NavLink>
              ) : (
                /* No `aria-disabled`: a span has no widget role for it to
                   apply to. Unlike the sidebar, this sheet already renders the
                   reason as VISIBLE text — phones have no hover — so assistive
                   tech reads it the same way a sighted reader does, and nothing
                   else is needed. */
                <span
                  key={entry.to}
                  className="flex min-h-12 items-center gap-3 rounded-[10px] px-3 py-2.5 text-body font-medium text-ink3"
                >
                  <span aria-hidden>{entry.icon}</span>
                  <span className="grow">
                    {entry.label}
                    {/* Visible, not a tooltip: phones have no hover. */}
                    <span className="block text-chip font-normal">{entry.lockedReason}</span>
                  </span>
                  <Lock size={14} aria-hidden />
                </span>
              ),
            )}
          </nav>
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}

/** Offline / failed-refresh banner (docs/03 §3, §6). */
function OfflineBanner(): ReactNode {
  const [online, setOnline] = useState(() => navigator.onLine);
  useEffect(() => {
    const up = (): void => {
      setOnline(true);
    };
    const down = (): void => {
      setOnline(false);
    };
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);
  if (online) return null;
  return (
    <div
      role="alert"
      className="bg-amber-bg px-3 py-2 text-center text-meta font-semibold text-amber-fg"
    >
      You're offline — nothing can save until the connection returns.
    </div>
  );
}

export function AppShell({ me }: { readonly me: Me }): ReactNode {
  const navigate = useNavigate();

  // Cmd/Ctrl+K jumps to search from anywhere (docs/04 §3 top bar).
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        void navigate('/search');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [navigate]);

  const { density } = usePreferences();
  // One request for the counts every navigation surface shows (punch item 1.6).
  const { counts } = useBadges();
  const actionItems = counts?.actionItems.total ?? 0;
  const unreadNotifications = counts?.notifications.unread ?? 0;
  // Mounted once, here: the shell wraps every authenticated screen, so this is
  // the one place the stored theme can be stamped without every list page
  // registering its own media listener (app/theme.ts).
  useApplyTheme();
  const { pathname } = useLocation();

  // Permission-gated entries resolve against the SERVER's permission set
  // (docs/03 §4) — locked with a visible reason, never silently missing.
  const nav = NAV.map((entry) =>
    entry.permissionKey !== undefined && !me.permissions.includes(entry.permissionKey)
      ? { ...entry, lockedReason: 'Requires administrator access' }
      : entry,
  );

  // Everything the bottom bar does not carry lives in the More sheet.
  const [moreOpen, setMoreOpen] = useState(false);
  const BAR_ROUTES = ['/', '/properties', '/work'];
  const moreEntries = nav.filter((entry) => !BAR_ROUTES.includes(entry.to));
  // The More button lights up when the CURRENT screen is one it owns, the same
  // truth NavLink computes for the other four.
  const moreActive = moreEntries.some(
    (entry) => entry.lockedReason === undefined && pathname.startsWith(entry.to),
  );

  useEffect(() => {
    document.body.dataset['density'] = density;
  }, [density]);

  // docs/07: environment behavior is driven by APP_ENV, never by hostname.
  // healthz reports the server's env; the banner follows it.
  const health = useHealth();
  const isTest = health.data?.env === 'test';
  /**
   * Both halves of the build, because a deploy replaces TWO containers and can
   * replace only one. The api reports itself through /healthz; this bundle
   * knows its own stamp (vite define). When they disagree the browser is
   * running code from a different release than the server — which is exactly
   * the "I deployed and nothing changed" failure, and it used to be invisible
   * because the pill showed the API's number either way.
   */
  const apiBuild = health.data?.version;
  const webBuild = __PROPOS_WEB_BUILD__;
  const mismatched = apiBuild !== undefined && apiBuild !== webBuild;

  return (
    <ToastProvider>
      {/* h-dvh + overflow-hidden makes the shell the viewport: nothing here
          scrolls, so the panes below can each own their own scrolling. dvh
          rather than vh so a phone's collapsing browser chrome is accounted
          for instead of pushing the bottom nav out of reach. */}
      <div className="flex h-dvh flex-col overflow-hidden bg-canvas">
        <RouteAnnouncer />
        {isTest ? <TestEnvBanner /> : null}
        {/* Above the offline notice on purpose: being someone else changes what
            every screen below means, which offline does not. */}
        <ViewAsBanner me={me} />
        <OfflineBanner />
        {/* min-h-0 is load-bearing: without it a flex child refuses to shrink
            below its content and the inner overflow never engages. */}
        <div className="flex min-h-0 grow">
          {/* Full sidebar (≥992) / icon rail (768–991) */}
          {/* Frozen: the pane is the full height of the shell and scrolls only
              if the module list outgrows it (or the viewport is short), which is
              what `overflow-y-auto` means here — a scrollbar when needed, never
              otherwise. overscroll-contain stops a flick that reaches the end of
              the nav from continuing into the content behind it. */}
          <nav
            aria-label="Primary"
            className="hidden w-14 shrink-0 flex-col gap-0.5 overflow-y-auto overscroll-contain bg-brand-950 p-2 sm:flex md:w-60 md:p-3"
          >
            <div className="mb-3 hidden px-2 md:flex">
              <BrandMark />
            </div>
            {nav.map((entry) => (
              <SideNavLink key={entry.to} entry={entry} />
            ))}
            {/* Identity, not a control — the account MENU lives on the top-right
                avatar and only there. mt-auto pins this to the foot of the pane
                when the module list is short, and contributes nothing when the
                list is long enough to scroll. shrink-0 keeps it from being
                squeezed away on a short viewport. */}
            <div className="mt-auto hidden shrink-0 items-center gap-2 px-2 pt-3 md:flex">
              <span className="min-w-0 grow">
                <span className="block truncate text-[13px] font-semibold text-on-green">
                  {me.displayName}
                </span>
                <span className="block truncate text-chip text-on-green-sub">
                  {me.roleKey ?? ''}
                </span>
              </span>
              {/* Environment pill + build stamp (docs/05 sidebar foot; docs/07).
                  Development shows no pill — it marks REAL environments — but
                  the build stamp always renders, so "which build am I looking
                  at" is answerable from any screen. */}
              <span className="flex shrink-0 flex-col items-end gap-0.5">
                {health.data?.env === 'test' || health.data?.env === 'production' ? (
                  // The env tokens, not the status palette: on amber/ok this
                  // pill read as a status chip, so production announced itself
                  // in the same green the app uses for "Active". Sentence case
                  // because docs/04 §7 bans all-caps label text.
                  <span
                    className={
                      'rounded-full px-2 py-0.5 text-chip font-semibold ' +
                      (isTest ? 'bg-env-test text-env-test-text' : 'bg-brand-700 text-surface')
                    }
                  >
                    {isTest ? 'Test' : 'Production'}
                  </span>
                ) : null}
                <span
                  className={
                    mismatched
                      ? 'rounded-full bg-amber-bg px-2 text-chip font-semibold text-amber-fg'
                      : 'text-chip text-on-green-sub'
                  }
                  title={
                    mismatched
                      ? `This page is build ${webBuild}; the server is running ${apiBuild}. ` +
                        'Reload; if it persists, the deploy replaced only one half — redeploy.'
                      : `Build ${webBuild} — quote this when reporting a problem`
                  }
                >
                  {mismatched ? `${webBuild} ≠ ${apiBuild}` : webBuild}
                </span>
              </span>
            </div>
          </nav>

          <div className="flex min-w-0 grow flex-col">
            {/* Top bar. Three columns from 768 up — an empty gutter, the
                search, then the actions — because equal 1fr gutters are what
                actually centres the search ON THE PAGE. A flex row with
                `mx-auto` would centre it in the space the actions leave over,
                which reads as slightly-off rather than centred. Below 768 it
                stays a flex row: there is no room for gutters, and the search
                wants every pixel. */}
            <header className="flex shrink-0 items-center gap-2 border-b border-hairline bg-card px-3 py-2 sm:grid sm:grid-cols-[1fr_minmax(0,28rem)_1fr]">
              {/* The left gutter: empty, except on test, where it carries the
                  Dev-only QA panel (L-36). Here and not beside the bell: the
                  two 1fr gutters are what keep search centered (ADR-31), and the
                  right one has no room left at 768. Decided by the server's
                  APP_ENV; its data loads only when it opens. */}
              {isTest ? (
                <div className="shrink-0 sm:justify-self-start">
                  <QaButton />
                </div>
              ) : (
                <div aria-hidden className="hidden sm:block" />
              )}
              <button
                type="button"
                aria-label="Search"
                onClick={() => void navigate('/search')}
                /* min-w-0 is what lets the label truncate: a flex item's
                   default min-width is its content, so without it the button
                   refuses to shrink and pushes the bell and avatar off a
                   375px screen — which the shell's overflow-hidden then hides
                   rather than reveals. */
                className="flex min-h-11 min-w-0 grow items-center gap-2 rounded-ctl border border-card-line px-3 text-left text-body text-ink2"
              >
                <Search size={17} aria-hidden />
                <span className="truncate">Search properties, tenants, vendors…</span>
              </button>
              <div className="flex items-center gap-1 sm:justify-self-end">
                {/* The My Work count, on the surface that HAS the space for it.
                    The bottom nav carries it below 768 and is hidden above, so
                    without this the number simply did not exist on a laptop —
                    which is where the work actually gets done. Rendered only
                    when there is something to say: a "0" badge is noise that
                    trains people to stop looking at the badge. */}
                {actionItems > 0 ? (
                  <RouterLink
                    to="/work"
                    aria-label={`Action items — ${String(actionItems)} needing attention`}
                    className="hidden min-h-11 items-center gap-1.5 rounded-ctl px-2 text-ink2 no-underline hover:bg-surface-hover hover:text-ink sm:flex"
                  >
                    <ClipboardList size={19} aria-hidden />
                    <CountPill count={actionItems} label="items in Action items" />
                  </RouterLink>
                ) : null}
                <NotificationBell unread={unreadNotifications} />
                {/* The app's only account button. Density lives in this menu
                    (docs/04 §5): a set-once comfort preference, not a control
                    for the list in front of you. */}
                <UserMenu me={me}>
                  <Avatar name={me.displayName} />
                </UserMenu>
              </div>
            </header>

            {/* The content scroller. Sticky toolbars inside a screen stick to
                the top of THIS box, immediately under the top bar. */}
            {/**
             * `relative` is load-bearing and was the second scrollbar (docs/17
             * 3F.1). Tailwind's `sr-only` is `position: absolute`, so EVERY
             * screen-reader-only span in the app is absolutely positioned — and
             * with no positioned ancestor its containing block is the initial
             * containing block, i.e. the document. One of them (the Pager's
             * " — This is the first page") sat 3,671px down a long property
             * list, which made `documentElement.scrollHeight` 3,672 and gave the
             * WINDOW a scrollbar beside `main`'s. Two scrollbars, and the wheel
             * did whichever the pointer was over.
             *
             * Measured, not guessed: the punch list's own hypothesis was a
             * missing `min-h-0`, and every `min-h-0` in the chain was already
             * correct. `relative` makes `main` the containing block for those
             * spans, so they are clipped by the pane that owns the scrolling.
             */}
            <main id="main" className="relative min-h-0 grow overflow-y-auto p-4 pb-24 sm:pb-4">
              <Outlet />
            </main>
          </div>
        </div>

        {/* Bottom nav (<768): Home · Properties · My work · Search · More (docs/05) */}
        <nav
          aria-label="Primary"
          className="fixed inset-x-0 bottom-0 flex justify-around border-t border-brand-800 bg-brand-950 pb-[env(safe-area-inset-bottom)] sm:hidden"
        >
          {[
            { to: '/', label: 'Home', icon: <House size={20} /> },
            { to: '/properties', label: 'Properties', icon: <Building2 size={20} /> },
            { to: '/work', label: 'Action items', icon: <ClipboardList size={20} />, badge: true },
            { to: '/search', label: 'Search', icon: <Search size={20} /> },
          ].map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                `relative flex min-h-12 min-w-11 flex-col items-center justify-center gap-0.5 px-2 py-1.5 text-[10px] font-medium no-underline ` +
                (isActive ? 'text-on-green' : 'text-on-green-nav')
              }
            >
              <span aria-hidden className="relative">
                {item.icon}
                {item.badge === true && actionItems > 0 ? (
                  <span className="absolute -right-2 -top-1">
                    <CountPill count={actionItems} label="items in Action items" />
                  </span>
                ) : null}
              </span>
              {item.label}
            </NavLink>
          ))}
          {/* A BUTTON, not a link: More opens the sheet with every module the
              bar does not carry. It was a placeholder link to /design. */}
          <button
            type="button"
            onClick={() => {
              setMoreOpen(true);
            }}
            aria-haspopup="dialog"
            className={
              `relative flex min-h-12 min-w-11 flex-col items-center justify-center gap-0.5 px-2 py-1.5 text-[10px] font-medium ` +
              (moreActive ? 'text-on-green' : 'text-on-green-nav')
            }
          >
            <span aria-hidden>
              <Menu size={20} />
            </span>
            More
          </button>
        </nav>
        <MoreSheet entries={moreEntries} open={moreOpen} onOpenChange={setMoreOpen} />
      </div>
    </ToastProvider>
  );
}
